// Phase 8D — SIKA Conversation History Rail.
//
// Displays past conversations grouped by date (Today, Yesterday, This week, Older).
// Each entry shows the first user message as a title preview.
// Clicking loads that session. "New conversation" starts fresh.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { getBrowserClient } from "../../supabase/browser.ts";
import { useAuthSession } from "../../auth/useAuthSession.ts";

export interface ConversationEntry {
  sessionId: string;
  goalId: string | null;
  title: string;
  lastMessageAt: string;
  messageCount: number;
  goalTitle?: string;
}

interface Props {
  activeSessionId: string | null;
  onSelectSession: (sessionId: string, goalId: string | null) => void;
  onNewConversation: () => void;
}

function timeAgoGroup(dateStr: string): string {
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return "This week";
  if (diffDays < 30) return "This month";
  return "Older";
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max).trim() + "...";
}

export function SikaHistoryRail({ activeSessionId, onSelectSession, onNewConversation }: Props): JSX.Element {
  const { session } = useAuthSession();
  const supabase = useMemo(() => getBrowserClient(), []);
  const [conversations, setConversations] = useState<ConversationEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const loadConversations = useCallback(async () => {
    if (!session?.user) return;
    setLoading(true);

    // Get distinct sessions with their first user message and latest timestamp
    const { data, error } = await supabase
      .from("sika_messages")
      .select("session_id, goal_id, role, content, created_at")
      .eq("user_id", session.user.id)
      .not("session_id", "is", null)
      .order("created_at", { ascending: false })
      .limit(500);

    if (error || !data) {
      setLoading(false);
      return;
    }

    // Group by session_id
    const sessionMap = new Map<string, {
      sessionId: string;
      goalId: string | null;
      firstUserMsg: string;
      lastAt: string;
      count: number;
    }>();

    for (const row of data) {
      const sid = row.session_id as string;
      if (!sid) continue;

      const existing = sessionMap.get(sid);
      if (!existing) {
        sessionMap.set(sid, {
          sessionId: sid,
          goalId: row.goal_id as string | null,
          firstUserMsg: row.role === "user" ? (row.content ?? "") : "",
          lastAt: row.created_at,
          count: 1,
        });
      } else {
        existing.count++;
        // If we haven't found a user message yet and this is one, use it
        if (!existing.firstUserMsg && row.role === "user") {
          existing.firstUserMsg = row.content ?? "";
        }
        // Update goal_id if found
        if (row.goal_id && !existing.goalId) {
          existing.goalId = row.goal_id as string;
        }
      }
    }

    // We need to find the first user message for each session (data was desc-sorted)
    // Re-query just user messages in ascending order for title extraction
    const sessionIds = Array.from(sessionMap.keys());
    if (sessionIds.length > 0) {
      const { data: userMsgs } = await supabase
        .from("sika_messages")
        .select("session_id, content")
        .eq("user_id", session.user.id)
        .eq("role", "user")
        .in("session_id", sessionIds)
        .order("created_at", { ascending: true })
        .limit(200);

      // First user message per session = the conversation title
      const firstMsgs = new Map<string, string>();
      for (const msg of userMsgs ?? []) {
        const sid = msg.session_id as string;
        if (sid && !firstMsgs.has(sid)) {
          firstMsgs.set(sid, msg.content ?? "");
        }
      }

      for (const [sid, entry] of sessionMap) {
        const firstMsg = firstMsgs.get(sid);
        if (firstMsg) entry.firstUserMsg = firstMsg;
      }
    }

    // Fetch goal titles for sessions linked to goals
    const goalIds = Array.from(new Set(
      Array.from(sessionMap.values())
        .map((e) => e.goalId)
        .filter(Boolean) as string[],
    ));

    let goalTitles = new Map<string, string>();
    if (goalIds.length > 0) {
      const { data: goals } = await supabase
        .from("sika_goals")
        .select("id, title")
        .in("id", goalIds);
      if (goals) {
        goalTitles = new Map(goals.map((g) => [g.id, g.title]));
      }
    }

    // Build sorted list
    const entries: ConversationEntry[] = Array.from(sessionMap.values())
      .map((e) => ({
        sessionId: e.sessionId,
        goalId: e.goalId,
        title: e.firstUserMsg || "New conversation",
        lastMessageAt: e.lastAt,
        messageCount: e.count,
        goalTitle: e.goalId ? goalTitles.get(e.goalId) : undefined,
      }))
      .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());

    setConversations(entries);
    setLoading(false);
  }, [supabase, session]);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  // Group conversations by time
  const groups = useMemo(() => {
    const grouped = new Map<string, ConversationEntry[]>();
    for (const c of conversations) {
      const group = timeAgoGroup(c.lastMessageAt);
      const arr = grouped.get(group) ?? [];
      arr.push(c);
      grouped.set(group, arr);
    }
    return grouped;
  }, [conversations]);

  return (
    <div className="goals-rail">
      <div className="gr-head">
        <div className="ttl">Conversations</div>
        <button type="button" className="new-goal" onClick={onNewConversation}>
          <Icon name="plus" size={14} />
          New chat
        </button>
      </div>
      <div className="gr-body">
        {loading ? (
          <div className="gr-section">Loading...</div>
        ) : conversations.length === 0 ? (
          <div className="gr-section" style={{ color: "var(--sika-text-muted, #606060)", fontSize: 13, padding: "16px 0" }}>
            No conversations yet. Start one!
          </div>
        ) : (
          Array.from(groups.entries()).map(([group, entries]) => (
            <div key={group}>
              <div className="gr-section">{group}</div>
              {entries.map((c) => (
                <button
                  key={c.sessionId}
                  type="button"
                  className={`goal-tile ${activeSessionId === c.sessionId ? "active" : ""}`}
                  aria-current={activeSessionId === c.sessionId ? "true" : undefined}
                  onClick={() => onSelectSession(c.sessionId, c.goalId)}
                >
                  <div className="gt-row1">
                    <span className="gt-status" style={{
                      background: c.goalId
                        ? "var(--sika-accent, #C5A028)"
                        : "var(--sika-text-muted, #606060)",
                    }} />
                    <span className="gt-name">{truncate(c.title, 40)}</span>
                  </div>
                  {c.goalTitle && (
                    <div className="gt-meta" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <Icon name="target" size={10} />
                      {c.goalTitle}
                    </div>
                  )}
                  <div className="gt-meta">
                    {c.messageCount} messages
                  </div>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
